import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, RefreshCw } from 'lucide-react';
import { SEO } from '../../components/SEO';
import useSEO, { seoConfigs } from '../../hooks/useSEO';

const MASCOTS = [
    {
        id: 'plumber',
        img: '/images/error/dog_plumber.png',
        titleKey: 'error.dogTitle',
        msgKey: 'error.dogMsg',
        color: 'bg-blue-50 text-blue-600'
    },
    {
        id: 'electrician',
        img: '/images/error/cat_electrician.png',
        titleKey: 'error.catTitle',
        msgKey: 'error.catMsg',
        color: 'bg-amber-50 text-amber-600'
    },
    {
        id: 'painter',
        img: '/images/error/hamster_painter.png',
        titleKey: 'error.hamsterTitle',
        msgKey: 'error.hamsterMsg',
        color: 'bg-indigo-50 text-indigo-600'
    }
];

export const ErrorPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [mascot, setMascot] = useState(MASCOTS[0]);

    // SEO for Error Page (Ensures noindex)
    const seoProps = useSEO(seoConfigs.notFound());

    useEffect(() => {
        // Pick a random mascot on mount
        const random = MASCOTS[Math.floor(Math.random() * MASCOTS.length)];
        setMascot(random);
    }, []);

    const handleRetry = () => {
        window.location.reload();
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 text-center font-sans">
            <SEO {...seoProps} />
            <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                {/* Image Area */}
                <div className={`h-64 sm:h-80 ${mascot.color} flex items-center justify-center relative overflow-hidden bg-opacity-30`}>
                    <div className="absolute inset-0 bg-gradient-to-t from-white to-transparent opacity-90" />
                    <img
                        src={mascot.img}
                        alt="Funny Mascot"
                        className="h-full w-full object-contain relative z-10 scale-90 hover:scale-100 transition-transform duration-500"
                     loading="lazy"/>
                </div>

                {/* Content */}
                <div className="p-8 relative z-20 -mt-12">
                    <h1 className="text-3xl font-bold text-gray-900 mb-3">
                        {t(mascot.titleKey, 'Oups ! Une erreur inattendue.')}
                    </h1>
                    <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                        {t(mascot.msgKey, "Cette page semble avoir disparu ou une erreur s'est produite.")}
                    </p>

                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={handleRetry}
                            className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                        >
                            <RefreshCw size={20} />
                            {t('notFound.retry')}
                        </button>
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center justify-center gap-2 px-6 py-3.5 bg-gray-100 text-gray-700 font-bold rounded-xl hover:bg-gray-200 transition"
                        >
                            <Home size={20} />
                            {t('notFound.home')}
                        </button>
                    </div>
                </div>
            </div>

            <p className="mt-8 text-gray-400 text-sm">
                Error 404 • Bricoler c'est aussi réparer les erreurs
            </p>
        </div>
    );
};
